import clsx from "clsx";
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";

import { Icon } from "./Icon";

/*
  Three kinds of chip, each with its own job:
  - a filter chip narrows what a view shows (the discipline chips in the bar);
  - a tag chip is a value the person has picked, and can take away (a "needed by" deliverable);
  - a prompt chip sends a line of text to the agent. It sends text and nothing else: no chip ever accepts, validates or
    declines a card (card by card, no accept-all).
*/

export interface FilterChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** The filter is on. It is told by the pressed state, not by colour alone. */
  selected?: boolean;
  /** A small number after the label ("N more"). */
  count?: number;
}

/** A pill-shaped toggle that narrows what a view shows. */
export function FilterChip({ selected = false, count, className, type = "button", children, ...rest }: FilterChipProps) {
  return (
    <button type={type} aria-pressed={selected} className={clsx("filter-chip", className)} {...rest}>
      {children}
      {count !== undefined && (
        <>
          {" "}
          <span className="filter-chip__count">{count}</span>
        </>
      )}
    </button>
  );
}

export interface TagChipProps extends HTMLAttributes<HTMLSpanElement> {
  /** Shows a remove button. Without it the chip is a plain tag. */
  onRemove?: () => void;
  /** Names the remove button. Defaults to "Remove" and the chip's text. */
  removeLabel?: string;
  children: ReactNode;
}

/** A picked value with an optional remove button ("Pump datasheet x"). */
export function TagChip({ onRemove, removeLabel, className, children, ...rest }: TagChipProps) {
  const name = removeLabel ?? (typeof children === "string" ? `Remove ${children}` : "Remove");
  return (
    <span className={clsx("tag-chip", className)} {...rest}>
      {children}
      {onRemove && (
        <button type="button" className="tag-chip__remove" aria-label={name} title={name} onClick={onRemove}>
          <Icon name="x" size={11} />
        </button>
      )}
    </span>
  );
}

export interface AddChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label" | "children"> {
  /** What the chip adds, in words: "Add a deliverable". The chip itself shows only a plus. */
  label: string;
}

/** The "+" chip at the end of a row of tag chips. */
export function AddChip({ label, className, type = "button", ...rest }: AddChipProps) {
  return (
    <button type={type} className={clsx("tag-chip", "tag-chip--add", className)} aria-label={label} title={label} {...rest}>
      <Icon name="plus" size={11} />
    </button>
  );
}

export type PromptChipProps = ButtonHTMLAttributes<HTMLButtonElement>;

/** A suggested line for the chat. Pressing it sends that text, with the screen's context; it changes nothing else. */
export function PromptChip({ className, type = "button", ...rest }: PromptChipProps) {
  return <button type={type} className={clsx("prompt-chip", className)} {...rest} />;
}
